import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { firstValueFrom, Observable } from 'rxjs';

export interface StatusToggleConfig {
  itemList: any[];
  itemName: string; // e.g., 'ad', 'announcement', 'category'
  titleField?: string; // field name to get item title, defaults to 'title'
  statusUpdateMethod: (uid: number, status: number) => Observable<any>;
  onSuccess?: () => Promise<void> | void;
}

@Injectable({
  providedIn: 'root'
})
export class AdsDashboardService { 

    constructor(
        private spinner: NgxSpinnerService,
        private popUpDialogService: PopUpDialogService
    ) { }

    enable(uid: number, config: StatusToggleConfig): void {
        const item = config.itemList.find(i => i.uid === uid);
        const titleField = config.titleField || 'title';
        const itemTitle = item?.[titleField] || `this ${config.itemName}`;
        
        
        this.popUpDialogService.openStatusActionDialog(
            `Are you sure you want to enable "${itemTitle}"?`,
            'ENABLE',
            'CANCEL',
            async () => {
                await this.changeStatus(uid, 0, config);  // Changed from 1 to 0
            },
            () => {
                console.log('❌ Disable cancelled by user');
            }
        );
    }

    /**
     * Enable an item
     */
    disable(uid: number, config: StatusToggleConfig): void {
        const item = config.itemList.find(i => i.uid === uid);
        const titleField = config.titleField || 'title';
        const itemTitle = item?.[titleField] || `this ${config.itemName}`;
        
        
        this.popUpDialogService.openStatusActionDialog(
            `Are you sure you want to disable "${itemTitle}"?`,
            'DISABLE',
            'CANCEL',
            async () => {
                await this.changeStatus(uid, 1, config);  // Changed from 0 to 1
            },
            () => {
                console.log('❌ Enable cancelled by user');
            }
        );
    }

    /**
     * Change status of an item
     * @param uid - Item unique identifier
     * @param value - New status value (0 = enabled, 1 = disabled)
     * @param config - Configuration for status toggle
     */
    private async changeStatus(uid: number, value: number, config: StatusToggleConfig): Promise<void> {
        try {
            this.spinner.show().catch((err) => {
                console.error('Spinner show error:', err);
            });


            // Call the status update method from config
            const response = await firstValueFrom(
                config.statusUpdateMethod(uid, value)
            );


            if (response.status === 200) {
                // Update local state
                const item = config.itemList.find(i => i.uid === uid);
                if (item) {
                    item.status = value;
                }
                
                // Call onSuccess callback if provided
                if (config.onSuccess) {
                    await config.onSuccess();
                }
                
                const action = value === 0 ? 'enabled' : 'disabled';
                this.popUpDialogService.openSuccessDialog(
                    `${this.capitalizeFirstLetter(config.itemName)} has been ${action} successfully!`
                );
                
                setTimeout(() => {
                    this.spinner.hide().catch((err) => {
                        console.error('Spinner hide error:', err);
                    });
                }, 500);
            } else {
                this.popUpDialogService.openErrorDialog(
                    'Failed to update status. Please try again.'
                );
                
                setTimeout(() => {
                    this.spinner.hide().catch((err) => {
                        console.error('Spinner hide error:', err);
                    });
                }, 500);
            }

        } catch (error) {
            const errorMessage = error?.error?.message || error?.message || 'Something went wrong!';
            this.popUpDialogService.openErrorDialog(errorMessage);
            
            this.spinner.hide().catch((err) => {
                console.error('Spinner hide error:', err);
            });
        }
    }

    /**
     * Helper method to capitalize first letter
     */
    private capitalizeFirstLetter(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
